import { FluentVariable } from '@fluent/bundle';
import { ReactLocalization, useLocalization } from '@fluent/react';
import { useRef } from 'react';

export function useCwL10n(): CwL10n {
    const { l10n } = useLocalization();
    const cwL10nRef = useRef<CwL10n | undefined>(undefined);

    if (cwL10nRef.current == null) {
        cwL10nRef.current = new CwL10n(l10n);
    }

    return cwL10nRef.current;
}

// This should match the same signatures
// we use in the I18nService from @cw-ng/i18n
// as close as possible.
export class CwL10n {

    public constructor(private readonly _l10n: ReactLocalization) {}

    /**
     * Returns the message for the `messageKey` and `layoutId`.
     * @param defaultMessage
     * @param messageKey
     * @param layoutId
     */
    public get(defaultMessage: string, messageKey: string, layoutId: string): string;
    /**
     * Returns the message for the `messageKey` and `layoutId`. The message will be processed with `data`, if provided.
     * @example
     * ```ts
     * const message = cwI10n.get('Hello, { $user }', 'text_body_greeting', 'mainPage', { user: 'John Doe' })
     * ```
     * @param defaultMessage
     * @param messageKey
     * @param layoutId
     * @param data - the record that contains the data for the variable replacement
     */
    public get<TData extends Record<string, FluentVariable>>(defaultMessage: string, messageKey: string, layoutId: string, data: TData): string;
    public get(...args: unknown[]): string {

        let defaultMessage = args[0] as string;
        const key = args[1] as string;
        const layoutId = args[2] as string;
        const data = args[3] as (Record<string, FluentVariable> | undefined);
        const warn = console.warn;
        console.warn = () => {};

        const regexTemplate = /\{\s*\$key\s*\}/.source;
        Object.entries(data ?? {}).forEach(([key, value]) => {
            // To make this more robust we should regex escape the key
            // but for now just assume the key is always a match for "\$[a-z0-9_]+"
            // so it doesn't need escaping.
            const regex = new RegExp(regexTemplate.replace('key', key), 'g')
            defaultMessage = defaultMessage.replace(regex, value as string);
        });

        const retVal = this._l10n.getString(`${layoutId}-${key}`, data, defaultMessage);
        console.warn = warn;
        return retVal;
    }
}
